import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  deleteUser,
  reload,
  getIdTokenResult,
} from "firebase/auth";
import {
  authErrorForUser,
  getPasswordFeedback,
} from "../utils/authUserMessages";
import { authLog } from "../utils/authDebugLog";

// Firebase configuration - values will be provided by user
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "placeholder-api-key",
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "placeholder.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "placeholder-project",
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "placeholder.appspot.com",
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "000000000000",
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID || "1:000000000000:web:placeholder",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

/**
 * Whether the account is verified for app + server session.
 * Uses reload() + ID token claims so we match /api/auth/set-cookie (stale user.emailVerified caused double-login).
 */
export async function isEmailVerifiedForSession(user) {
  if (!user) return false;
  try {
    await reload(user);
  } catch {
    // continue; token check may still work
  }
  if (user.emailVerified) return true;
  try {
    const tr = await getIdTokenResult(user, true);
    if (tr.claims.email_verified === true) {
      authLog("auth:verifiedViaIdToken", { uidTail: user.uid?.slice(-8) });
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

// Sign up with email verification
export const signUp = async (email, password, name, role) => {
  try {
    const passwordHint = getPasswordFeedback(password);
    if (passwordHint) {
      return {
        success: false,
        error: passwordHint,
      };
    }

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    await sendEmailVerification(userCredential.user);

    // Store user in database with role and name
    const token = await userCredential.user.getIdToken();

    const response = await fetch("/api/auth/register", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token,
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        name,
        role,
      }),
    });

    const data = await response.json();

    if (!data.success) {
      // If database registration fails, we should probably delete the Firebase user
      // but for now just throw the error
      throw new Error(data.error || "Failed to complete registration");
    }

    authLog("signUp:registerOk", {
      uidTail: userCredential.user.uid?.slice(-8),
    });

    return {
      success: true,
      user: userCredential.user,
      message: "Verification email sent. Please check your inbox.",
    };
  } catch (error) {
    console.error("Sign up error:", error);
    return {
      success: false,
      error: authErrorForUser(error, "signup"),
    };
  }
};

// Sign in
export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );

    const verified = await isEmailVerifiedForSession(userCredential.user);
    if (!verified) {
      // Keep Firebase session so Login can call resendVerificationEmail (auth.currentUser)
      return {
        success: false,
        error: "Please verify your email before signing in",
        requiresVerification: true,
      };
    }

    // Get ID token and set cookie
    const token = await userCredential.user.getIdToken();

    const response = await fetch("/api/auth/set-cookie", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token,
        uid: userCredential.user.uid,
        email: userCredential.user.email,
      }),
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error);
    }

    authLog("signIn:setCookieOk", {
      uidTail: userCredential.user.uid?.slice(-8),
    });

    return {
      success: true,
      user: userCredential.user,
    };
  } catch (error) {
    console.error("Sign in error:", error);
    return {
      success: false,
      error: authErrorForUser(error, "login"),
    };
  }
};

// Sign out — clears Firebase session and HTTP-only cookie. Throws on any failure.
export const logOut = async () => {
  await signOut(auth);

  const response = await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    // ignore non-JSON body
  }

  if (!response.ok || data.success === false) {
    const message =
      data.error ||
      (!response.ok
        ? `Could not clear server session (${response.status})`
        : "Logout failed");
    throw new Error(message);
  }
};

// Reset password
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return {
      success: true,
      message: "Password reset email sent. Please check your inbox.",
    };
  } catch (error) {
    console.error("Password reset error:", error);
    return {
      success: false,
      error: authErrorForUser(error, "login"),
    };
  }
};

// Resend verification email
export const resendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No user signed in");
    }

    await sendEmailVerification(user);
    return {
      success: true,
      message: "Verification email sent",
    };
  } catch (error) {
    console.error("Resend verification error:", error);
    return {
      success: false,
      error: authErrorForUser(error, "login"),
    };
  }
};

/**
 * Clears the HTTP-only session cookie without signing out of Firebase.
 * Use when the server has a stale JWT (e.g. DB reset) so we can set a fresh cookie for the current Firebase user.
 */
export const clearServerAuthCookie = async () => {
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    console.error("clearServerAuthCookie:", err);
  }
};

/**
 * Refresh the auth cookie with a fresh ID token (for already-logged-in users).
 * Call this proactively or when the server returns 401 due to expired token.
 * Retries transient 503 (e.g. idle MySQL pool) so focus/interval refresh can recover.
 * @returns {Promise<{ ok: boolean, needsVerification?: boolean }>}
 */
export const refreshAuthCookie = async () => {
  const user = auth.currentUser;
  if (!user) return { ok: false };

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  try {
    const token = await user.getIdToken(true);
    const body = JSON.stringify({
      token,
      uid: user.uid,
      email: user.email,
    });

    for (let attempt = 1; attempt <= 3; attempt++) {
      const response = await fetch("/api/auth/set-cookie", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body,
      });
      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      if (data.success === true) {
        authLog("refreshAuthCookie:ok", { uidTail: user.uid?.slice(-8) });
        return { ok: true };
      }
      if (response.status === 403 && data.requiresEmailVerification) {
        authLog("refreshAuthCookie:403Unverified", {
          uidTail: user.uid?.slice(-8),
        });
        return { ok: false, needsVerification: true };
      }
      const transient503 =
        response.status === 503 && data && data.temporary === true;
      if (transient503 && attempt < 3) {
        authLog("refreshAuthCookie:503Retry", {
          attempt,
          uidTail: user.uid?.slice(-8),
        });
        await wait(120 * attempt);
        continue;
      }
      authLog("refreshAuthCookie:fail", {
        status: response.status,
        err: data.error || "",
        uidTail: user.uid?.slice(-8),
      });
      return { ok: false };
    }
    return { ok: false };
  } catch (err) {
    console.error("Refresh auth cookie error:", err);
    authLog("refreshAuthCookie:exception", { message: err?.message || String(err) });
    return { ok: false };
  }
};

// Check authentication status
export const checkAuthStatus = () => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

export { auth };
